// use this module to prefetch as much dynamic data as we can as the app loads
import { Ref } from "vue";
import { AdTag, AdTagCampaign, Operator, UrlQuery } from "@fresh8/types/src";
import { getPublisherBet, getAdtag, getPreview, getJSON } from "./api";
import { BetData } from "@fresh8/types/src/betData";
import { randomOperator } from "./lib/ramdomise";
const searchParams = new URLSearchParams(window.location.search);

/**
 * It gets the adTag ref nad the page query string, and extract the data needed
 * to request the bet data from the publishers api.
 */
export const publisherBet = async (
  adTag?: Ref<AdTag | undefined>,
  selectedCampaign?: Ref<AdTagCampaign | undefined>,
  selectedOperator?: Ref<Operator | undefined>
): Promise<BetData> => {
  if (searchParams.has("betEndpoint")) {
    return getJSON(searchParams.get("betEndpoint") as string);
  }

  const operator = selectedOperator?.value;

  const queries: UrlQuery = {};
  searchParams.forEach((value, param) => {
    queries[param] = value;
  });

  const campaign = selectedCampaign?.value;

  const relevancy =
    typeof adTag?.value?.team !== "string" &&
    adTag?.value?.team?.features?.relevancy;

  /**
   * When an adtag has page relevancy enabled in the team features then
   * contextual should be using page relevancy to rank events.
   * primaryBehaviour=relevancy or secondaryBehaviour=relevancy
   * Also to get the contextualProductSetId from the operators object in the adtagData.
   * found at operators.contextualProductSetId
   * need to be added to the recommenders request using primaryProductSetId or secondaryProductSetId
   */
  const primaryBehaviour =
    relevancy === true &&
    adTag?.value?.behaviours?.primary.type === "contextual"
      ? "relevancy"
      : adTag?.value?.behaviours?.primary.type || "";

  const primaryProductSetId =
    adTag?.value?.behaviours?.primary.type === "productSet"
      ? adTag?.value.behaviours?.primary.productSets[operator?._id || ""]
          ?.productSetRef.productSetId
      : relevancy && adTag?.value?.behaviours?.primary.type === "contextual"
      ? operator?.contextualProductSetId || ""
      : "";

  // When smart ads are enabled for a team and a product set is set to be sorted by clicks, then the product set should serve the correct events sorted by click data.
  const primaryRecommender =
    adTag?.value?.behaviours?.primary.productSets[operator?._id || ""]
      ?.productSetRef.sortedBy === "SMARTADS"
      ? "smartads"
      : undefined;

  const secondaryBehaviour =
    relevancy === true &&
    adTag?.value?.behaviours?.secondary.type === "contextual"
      ? "relevancy"
      : adTag?.value?.behaviours?.secondary.type || "";
  const secondaryProductSetId =
    adTag?.value?.behaviours?.secondary.type === "productSet"
      ? adTag?.value.behaviours?.secondary.productSets[operator?._id || ""]
          ?.productSetRef.productSetId
      : relevancy && adTag?.value?.behaviours?.secondary.type === "contextual"
      ? operator?.contextualProductSetId || ""
      : "";

  // When smart ads are enabled for a team and a product set is set to be sorted by clicks, then the product set should serve the correct events sorted by click data.
  const secondaryRecommender =
    adTag?.value?.behaviours?.secondary.productSets[operator?._id || ""]
      ?.productSetRef.sortedBy === "SMARTADS"
      ? "smartads"
      : undefined;

  const campaignBehaviour = campaign?.campaignContent.type || "";
  const campaignProductSetId = campaign?.campaignContent?.productSetId || "";

  const { ref, sport, competitor, competition, useMockData, ...rest } = queries;

  // if the selected campaign is an offer-only one, we don't need request the recommender
  if (campaign?.campaignContent?.type === "offer-only") {
    return {
      offer: campaign.campaignContent.offer,
      betQuery: {
        primaryBehaviour,
        primaryProductSetId,
        secondaryBehaviour,
        secondaryProductSetId,
        campaignProductSetId,
        operatorKey: operator?.key,
        useMockData
      }
    } as BetData;
  }

  const data = await getPublisherBet(
    primaryBehaviour,
    primaryProductSetId,
    secondaryBehaviour,
    secondaryProductSetId,
    campaignBehaviour,
    campaignProductSetId,
    ref,
    sport,
    competitor,
    competition,
    operator?.key,
    useMockData,
    primaryRecommender,
    secondaryRecommender,
    rest
  );

  return {
    ...data.payload,
    analytics: {
      ...(data?.payload?.analytics || {}),
      catalogueId: data.catalogueId
    },
    betQuery: {
      primaryBehaviour,
      primaryProductSetId,
      secondaryBehaviour,
      secondaryProductSetId,
      campaignProductSetId,
      ref,
      sport,
      competitor,
      competition,
      operatorKey: operator?.key,
      useMockData
    }
  };
};

export const preview = (operator?: string) =>
  getPreview({
    competitors: searchParams.get("competitors") || undefined,
    competitions: searchParams.get("competitions") || undefined,
    marketIds: searchParams.get("marketIds") || undefined,
    marketTypes: searchParams.get("marketTypes") || undefined,
    limit: searchParams.get("limit") || undefined,
    operator: operator || searchParams.get("operator") || undefined,
    sport: searchParams.get("sport") || undefined,
    eventId: searchParams.get("eventId") || undefined
  });

export const adtag = (async () => {
  const adTagJson = await getAdtag(
    searchParams.get("id"),
    searchParams.get("useMockBrand") || undefined,
    searchParams.get("ref") || undefined
  );

  if (!adTagJson.operator?._id) {
    adTagJson.operator = JSON.parse(
      JSON.stringify(randomOperator(adTagJson.operators))
    );
  }
  return adTagJson;
})();

export const getPrototype = (): string | null => {
  return searchParams.get("prototype");
};
